import { useEffect, useState } from "react";
import { MailOpen, MousePointerClick, ChevronDown, ChevronRight, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useSortable, SortableTh } from "@/lib/sort";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)} %` : "—");
const FILTERS = [["all", "Todas"], ["opened", "Lo abrieron"], ["not_opened", "No lo abrieron"], ["clicked", "Pulsaron el enlace"]];

function ResendUnopened({ campaignId, count, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setOpen(false); setBusy(true);
    try {
      const { data } = await api.post(`/admin/campaign/${campaignId}/resend-unopened`);
      toast.success(`Reenviando a ${data.recipients_count} farmacias`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reenviar"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} disabled={!count || busy} data-testid={`campaign-resend-${campaignId}`}
        className="ml-auto inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-40">
        <Send className="h-3.5 w-3.5" /> Reenviar a quien no lo abrió ({count})
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="campaign-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reenviar a {count} farmacias?</AlertDialogTitle>
            <AlertDialogDescription>Recibirán el mismo correo las farmacias que aún no lo han abierto. Quien se dio de baja no lo recibirá. No se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-resend-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="campaign-resend-confirm">Sí, reenviar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RecipientsTable({ campaignId, canResend, onResent }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get(`/admin/campaign/${campaignId}/recipients`).then((r) => setRows(r.data)); }, [campaignId]);
  const filtered = (rows || []).filter((r) =>
    filter === "opened" ? r.opened_at : filter === "not_opened" ? !r.opened_at : filter === "clicked" ? r.clicked_at : true);
  const { sorted, sortKey, direction, toggle } = useSortable(filtered, "opened_at", "desc", `campaign-rcpt-${campaignId}`);
  if (!rows) return <div className="text-xs text-slate-500 p-3">Cargando…</div>;
  return (
    <div className="p-3 bg-slate-50 border-t border-slate-200">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`campaign-filter-${k}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"}`}>{label}</button>
        ))}
        {canResend && <ResendUnopened campaignId={campaignId} count={rows.filter((r) => r.status === "sent" && !r.opened_at).length} onDone={onResent} />}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs bg-white border border-slate-200" data-testid={`campaign-recipients-${campaignId}`}>
          <thead><tr className="bg-slate-100 text-slate-600">
            <SortableTh k="pharmacy_name" sortKey={sortKey} direction={direction} toggle={toggle}>Farmacia</SortableTh>
            <SortableTh k="email" sortKey={sortKey} direction={direction} toggle={toggle}>Email</SortableTh>
            <SortableTh k="opened_at" sortKey={sortKey} direction={direction} toggle={toggle}>Abierto</SortableTh>
            <SortableTh k="open_count" sortKey={sortKey} direction={direction} toggle={toggle} align="right">Veces</SortableTh>
            <SortableTh k="clicked_at" sortKey={sortKey} direction={direction} toggle={toggle}>Pulsó el enlace</SortableTh>
          </tr></thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.rid} className="border-t border-slate-100" data-testid={`campaign-rcpt-row-${r.rid}`}>
                <td className="px-3 py-1.5 font-medium">{r.pharmacy_name || "—"}</td>
                <td className="px-3 py-1.5 text-slate-600">{r.email}{r.status === "failed" && <span className="ml-1 text-rose-600">(no enviado)</span>}</td>
                <td className="px-3 py-1.5">{r.opened_at ? <span className="text-emerald-700">{fmtDate(r.opened_at)}</span> : <span className="text-slate-400">No</span>}</td>
                <td className="px-3 py-1.5 text-right font-mono-num">{r.open_count || 0}</td>
                <td className="px-3 py-1.5">{r.clicked_at ? <span className="text-sky-700 font-medium">{fmtDate(r.clicked_at)} · {r.click_count}×</span> : <span className="text-slate-400">No</span>}</td>
              </tr>
            ))}
            {!sorted.length && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Ninguna farmacia en este filtro</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CampaignHistory({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [refreshKey]);
  return (
    <section className="mt-10" data-testid="campaign-history">
      <div className="flex items-center gap-3">
        <h2 className="font-display text-lg font-bold">Quién lo ha abierto</h2>
        <button onClick={load} data-testid="campaign-history-refresh" className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-300 rounded-md px-2 py-1 hover:bg-slate-50"><RefreshCw className="h-3 w-3" /> Actualizar</button>
      </div>
      <p className="text-xs text-slate-500 mt-1">Es una cifra aproximada: algunos programas de correo bloquean las imágenes (entonces no cuenta como abierto) y otros las cargan solos (puede contar como abierto sin leerlo). Pulsar el enlace sí es seguro.</p>
      <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-white">
        {items === null && <div className="p-4 text-sm text-slate-500">Cargando…</div>}
        {items?.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="campaign-history-empty">Aún no has hecho ningún envío a todos.</div>}
        {items?.map((c) => (
          <div key={c.id} className="border-t first:border-t-0 border-slate-200">
            <button onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`campaign-row-${c.id}`} className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 hover:bg-slate-50">
              {open === c.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <div className="min-w-[220px] flex-1"><div className="text-sm font-semibold">{c.subject}{c.parent_id && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Reenvío</span>}</div><div className="text-xs text-slate-500">{fmtDate(c.sent_at)} · {c.sent} enviados{c.failed ? ` · ${c.failed} fallidos` : ""}{c.status === "running" ? " · enviando…" : ""}</div></div>
              <div className="text-sm flex items-center gap-1.5" data-testid={`campaign-opened-${c.id}`}><MailOpen className="h-4 w-4 text-emerald-600" /><strong>{c.opened}</strong> abiertos <span className="text-slate-400">({pct(c.opened, c.sent)})</span></div>
              <div className="text-sm flex items-center gap-1.5" data-testid={`campaign-clicked-${c.id}`}><MousePointerClick className="h-4 w-4 text-sky-600" /><strong>{c.clicked}</strong> pulsaron <span className="text-slate-400">({pct(c.clicked, c.sent)})</span></div>
            </button>
            {open === c.id && <RecipientsTable campaignId={c.id} canResend={c.status === "done" && !!c.content} onResent={load} />}
          </div>
        ))}
      </div>
    </section>
  );
}
